import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Envelope, Lock, Eye, EyeSlash, GoogleLogo, User, Phone, MapPin, Hash } from '@phosphor-icons/react';
import { Country, State, City } from 'country-state-city';
import SearchableSelect from '../components/SearchableSelect';
import { usePlatformLogo } from '../hooks/usePlatformSettings';
import { useInactiveCountries } from '../hooks/useInactiveCountries';

const BuyerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/buyer';
  const autoBookClassId = searchParams.get('autobook') || '';
  const { login, register, googleLogin } = useAuth();
  const LOGO_URL = usePlatformLogo();
  
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(location.state?.error || '');
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regCountry, setRegCountry] = useState('');
  const [regState, setRegState] = useState('');
  const [regCity, setRegCity] = useState('');
  const [regPincode, setRegPincode] = useState('');

  const countries = useMemo(() => 
    Country.getAllCountries().map(c => ({ value: c.isoCode, label: c.name })),
  []);
  const inactiveCountries = useInactiveCountries();
  const visibleCountries = useMemo(
    () => countries.filter(c => !inactiveCountries.has((c.label || '').toLowerCase())),
    [countries, inactiveCountries]
  );

  const states = useMemo(() => {
    if (!regCountry) return [];
    return State.getStatesOfCountry(regCountry).map(s => ({ value: s.isoCode, label: s.name }));
  }, [regCountry]);

  const cities = useMemo(() => {
    if (!regCountry || !regState) return [];
    return City.getCitiesOfState(regCountry, regState).map(c => ({ value: c.name, label: c.name }));
  }, [regCountry, regState]);

  const phoneCode = useMemo(() => {
    if (!regCountry) return '';
    const c = Country.getCountryByCode(regCountry);
    return c?.phonecode ? `+${c.phonecode}` : '';
  }, [regCountry]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(loginEmail, loginPassword);
    setLoading(false);
    if (result.success) {
      if (result.user?.role === 'admin' || result.user?.role === 'subadmin') {
        navigate('/admin', { replace: true });
      } else if (result.user?.role === 'seller' || result.user?.role === 'both') {
        // Sellers ALWAYS land on the Host Portal, even when logging in via /buyer/login.
        navigate('/seller', { replace: true });
      } else {
        // Honour ?redirect= param (e.g., /live-classes after Sign in to Book) with optional ?autobook=
        const finalUrl = autoBookClassId ? `${redirectTo}?autobook=${encodeURIComponent(autoBookClassId)}` : redirectTo;
        navigate(finalUrl, { replace: true });
      }
    } else {
      setError(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (regPassword !== regConfirmPassword) { setError('Passwords do not match'); return; }
    if (regPassword.length < 6) { setError('Password must be at least 6 characters'); return; }

    const countryName = regCountry ? Country.getCountryByCode(regCountry)?.name || regCountry : null;
    const stateName = regState ? State.getStateByCodeAndCountry(regState, regCountry)?.name || regState : null;
    const fullPhone = regPhone ? `${phoneCode}${regPhone}` : null;

    setLoading(true);
    const result = await register({
      name: regName, email: regEmail, password: regPassword,
      phone: fullPhone, country: countryName, state: stateName,
      city: regCity || null, pincode: regPincode || null, role: 'buyer'
    });
    setLoading(false);
    if (result.success) {
      const finalUrl = autoBookClassId ? `${redirectTo}?autobook=${encodeURIComponent(autoBookClassId)}` : redirectTo;
      navigate(finalUrl, { replace: true });
    }
    else { setError(result.error); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex flex-col items-center justify-center p-4">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-cyan-100/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/"><img src={LOGO_URL} alt="Look4Space" className="w-32 h-32 mx-auto mb-4 object-contain" /></Link>
          <p className="text-slate-500">Find and book spaces anywhere</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl shadow-blue-500/10 overflow-hidden">
          <div className="flex border-b border-slate-100">
            <button onClick={() => { setActiveTab('login'); setError(''); }}
              className={`flex-1 py-4 text-center font-semibold transition-colors ${activeTab === 'login' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="login-tab">Login</button>
            <button onClick={() => { setActiveTab('register'); setError(''); }}
              className={`flex-1 py-4 text-center font-semibold transition-colors ${activeTab === 'register' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="register-tab">Register</button>
          </div>

          <div className="p-6 sm:p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="error-message">{error}</div>
            )}

            {activeTab === 'login' && (
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Envelope className="text-slate-400" size={20} /></div>
                  <Input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="Email address" className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                    required data-testid="login-email-input" />
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Lock className="text-slate-400" size={20} /></div>
                  <Input type={showPassword ? 'text' : 'password'} value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)} placeholder="Password"
                    className="pl-12 pr-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                    required data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center">
                    {showPassword ? <EyeSlash className="text-slate-400 hover:text-slate-600" size={20} /> : <Eye className="text-slate-400 hover:text-slate-600" size={20} />}
                  </button>
                </div>
                <div className="flex items-center justify-end">
                  <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 font-medium">Forgot password?</Link>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/20" data-testid="login-submit-btn">
                  {loading ? <div className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>Signing in...</span></div> : 'Sign In'}
                </Button>
              </form>
            )}

            {activeTab === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><User className="text-slate-400" size={20} /></div>
                  <Input type="text" value={regName} onChange={(e) => setRegName(e.target.value)}
                    placeholder="Full name" className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                    required data-testid="register-name-input" />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Envelope className="text-slate-400" size={20} /></div>
                  <Input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="Email address" className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                    required data-testid="register-email-input" />
                </div>

                {/* Country */}
                <SearchableSelect value={regCountry}
                  onChange={(val) => { setRegCountry(val); setRegState(''); setRegCity(''); }}
                  options={visibleCountries} placeholder="Select country" icon={MapPin}
                  testId="register-country-select" accentColor="blue" />

                {/* State */}
                <SearchableSelect value={regState}
                  onChange={(val) => { setRegState(val); setRegCity(''); }}
                  options={states} placeholder={regCountry ? "Select state" : "Select country first"}
                  icon={MapPin} testId="register-state-select" accentColor="blue"
                  disabled={!regCountry} />

                {/* City */}
                <SearchableSelect value={regCity} onChange={setRegCity}
                  options={cities} placeholder={regState ? "Select city" : "Select state first"}
                  icon={MapPin} testId="register-city-select" accentColor="blue"
                  disabled={!regState} />

                {/* Pincode */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Hash className="text-slate-400" size={20} /></div>
                  <Input type="text" value={regPincode} onChange={(e) => setRegPincode(e.target.value)}
                    placeholder="Pin code" className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                    data-testid="register-pincode-input" />
                </div>

                {/* Phone with country code */}
                <div className="relative flex gap-2">
                  <div className="relative w-24 shrink-0">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Phone className="text-slate-400" size={18} /></div>
                    <input type="text" value={phoneCode} readOnly tabIndex={-1}
                      className="w-full pl-9 h-12 bg-slate-100 border border-slate-200 rounded-xl text-sm text-slate-700 font-medium text-center cursor-default outline-none"
                      placeholder="Code" data-testid="register-phone-code" />
                  </div>
                  <div className="flex-1">
                    <Input type="tel" value={regPhone} onChange={(e) => setRegPhone(e.target.value)}
                      placeholder="Phone number (optional)" className="h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                      data-testid="register-phone-input" />
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Lock className="text-slate-400" size={20} /></div>
                  <Input type={showPassword ? 'text' : 'password'} value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)} placeholder="Password"
                    className="pl-12 pr-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                    required data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center">
                    {showPassword ? <EyeSlash className="text-slate-400 hover:text-slate-600" size={20} /> : <Eye className="text-slate-400 hover:text-slate-600" size={20} />}
                  </button>
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Lock className="text-slate-400" size={20} /></div>
                  <Input type={showPassword ? 'text' : 'password'} value={regConfirmPassword}
                    onChange={(e) => setRegConfirmPassword(e.target.value)} placeholder="Confirm password"
                    className="pl-12 h-12 bg-slate-50 border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl"
                    required data-testid="register-confirm-password-input" />
                </div>

                <Button type="submit" disabled={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/20 mt-2"
                  data-testid="register-submit-btn">
                  {loading ? <div className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>Creating account...</span></div> : 'Create Account'}
                </Button>
              </form>
            )}

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-slate-500">or continue with</span></div>
            </div>

            <Button type="button" onClick={() => googleLogin()} variant="outline"
              className="w-full h-12 border-slate-200 hover:bg-slate-50 rounded-xl font-medium" data-testid="google-login-btn">
              <GoogleLogo size={20} weight="bold" className="mr-2 text-slate-700" />
              <span className="text-slate-700">Google</span>
            </Button>
          </div>
        </div>

        <p className="mt-6 text-center text-slate-500">
          Want to list your space?{' '}
          <Link to="/seller/login" className="text-blue-600 hover:text-blue-700 font-semibold" data-testid="seller-link">Become a Host</Link>
        </p>
      </div>
    </div>
  );
};

export default BuyerAuth;
